/**
 * Determines the specific type of a number value
 */
const getSpecificNumberType = (value: number) => {
  if (Number.isNaN(value)) {
    return 'NaN'
  }

  if (Number.isInteger(value)) {
    return 'integer'
  }
}

/**
 * Determines the specific type of an object value
 */
const getSpecificObjectType = (value: object | null) => {
  if (value === null) {
    return 'null'
  }

  if (Array.isArray(value)) {
    return 'array'
  }

  // Check against known object types
  for (const [Constructor, name] of [
    [Date, 'date'],
    [RegExp, 'regexp'],
    [Error, 'error'],
    [Promise, 'promise'],
    [Map, 'map'],
    [Set, 'set'],
  ] as const) {
    if (value instanceof Constructor) {
      return name
    }
  }
}

/**
 * Enhanced type detection function that provides more specific type information than `typeof`
 *
 * This function extends the built-in `typeof` operator by distinguishing between different
 * subtypes of numbers and objects, providing more granular type information useful for
 * runtime type checking and debugging.
 *
 * @param value - The value to check the type of
 * @returns A string representing the specific type of the value
 *
 * @example
 * ```typescript
 * // Basic types (same as typeof)
 * TypeOf('hello')        // 'string'
 * TypeOf(true)           // 'boolean'
 * TypeOf(undefined)      // 'undefined'
 * TypeOf(Symbol('x'))    // 'symbol'
 * TypeOf(123n)           // 'bigint'
 * TypeOf(() => {})       // 'function'
 *
 * // Enhanced number detection
 * TypeOf(42)             // 'integer'
 * TypeOf(3.14)           // 'number'
 * TypeOf(NaN)            // 'NaN'
 * TypeOf(Infinity)       // 'number'
 *
 * // Enhanced object detection
 * TypeOf([1, 2, 3])      // 'array'
 * TypeOf(null)           // 'null'
 * TypeOf(new Date())     // 'date'
 * TypeOf(/regex/)        // 'regexp'
 * TypeOf(new Error())    // 'error'
 * TypeOf(Promise.resolve()) // 'promise'
 * TypeOf(new Map())      // 'map'
 * TypeOf(new Set())      // 'set'
 * TypeOf({})             // 'object'
 * ```
 *
 * @remarks
 * **Number Types:**
 * - `'integer'` - Whole numbers (including negative integers)
 * - `'number'` - Non-integer numbers and special values like Infinity
 * - `'NaN'` - Not a Number values
 *
 * **Object Types:**
 * - `'array'` - Arrays and array-like objects created with Array constructor
 * - `'null'` - The null value
 * - `'date'` - Date objects
 * - `'regexp'` - Regular expression objects
 * - `'error'` - Error objects and their subclasses
 * - `'promise'` - Promise objects
 * - `'map'` - Map objects
 * - `'set'` - Set objects
 * - `'object'` - Plain objects and other object types not covered above
 *
 * **Primitive Types (same as typeof):**
 * - `'string'` - String values
 * - `'boolean'` - Boolean values
 * - `'undefined'` - Undefined value
 * - `'symbol'` - Symbol values
 * - `'bigint'` - BigInt values
 * - `'function'` - Function values
 */
export const TypeOf = (value: unknown) => {
  switch (typeof value) {
    case 'number': {
      return getSpecificNumberType(value) ?? 'number'
    }
    case 'object': {
      return getSpecificObjectType(value) ?? 'object'
    }
    default: {
      return typeof value
    }
  }
}

/**
 * Checks if a value is of a specific type
 *
 * @param value - The value to check the type of
 * @param expected - The type to check against
 * @returns True if the value is of the specified type, false otherwise
 */
export const isTypeOf = (value: unknown, expected: ReturnType<typeof TypeOf>) =>
  TypeOf(value) === expected
